/**
 * useServiceStages — 客户只读服务进度 hook（Issue #198）。
 *
 * 与 client-app 同名 hook 共享同一 OpenAPI 端点 /api/v1/me/service-stages。
 * 客户端不传入 customerId / tenantId / orgId；身份由后端从 access token 派生。
 *
 * 红线：
 *   - 不依赖 openapi-fetch 的 error 字段；ApiError 经由 try/catch 处理。
 *   - 不在 UI 中展示后端原始错误，统一通过 classifyMeFetchError 折射。
 *   - 404 = 客户档案尚未建立，作为 notFound 单独区分，便于 UI 文案克制。
 */
import { useCallback, useEffect, useState } from 'react';
import { ApiError } from '@jinzhuan/api-client';
import type { ApiComponents } from '@jinzhuan/api-client';
import {
  createCustomerMeApiClient,
  refreshCustomerSession,
} from './customerSession';
import { classifyMeFetchError, type MeFetchError } from './classify';

export type MeServiceStagesDto = ApiComponents['schemas']['MeServiceStagesDto'];
export type ServiceStageEventDto =
  ApiComponents['schemas']['ServiceStageEventDto'];

const TERMINAL_STAGES: ReadonlySet<MeServiceStagesDto['currentStage']> = new Set([
  'repurchased',
  'referred',
  'lost',
]);

export function isTerminalStage(stage: MeServiceStagesDto['currentStage']): boolean {
  return TERMINAL_STAGES.has(stage);
}

export interface UseServiceStagesResult {
  data: MeServiceStagesDto | null;
  loading: boolean;
  notFound: boolean;
  error: MeFetchError | null;
  refetch: () => void;
}

export function useServiceStages(): UseServiceStagesResult {
  const [data, setData] = useState<MeServiceStagesDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState<MeFetchError | null>(null);
  const [tick, setTick] = useState(0);

  const refetch = useCallback(() => {
    setTick((t) => t + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function load(): Promise<void> {
      setLoading(true);
      setNotFound(false);
      setError(null);

      try {
        const apiClient = createCustomerMeApiClient();
        const dto = (await apiClient.GET('/api/v1/me/service-stages')).data ?? null;
        if (cancelled) return;
        setData(dto);
      } catch (err: unknown) {
        if (cancelled) return;
        const classified = classifyMeFetchError(err);
        if (err instanceof ApiError && err.status === 404) {
          setNotFound(true);
          return;
        }
        if (classified.kind === 'session_expired') {
          const refreshed = await refreshCustomerSession();
          if (cancelled) return;
          if (refreshed) {
            try {
              const retryDto =
                (await createCustomerMeApiClient().GET('/api/v1/me/service-stages')).data ??
                null;
              setData(retryDto);
              setError(null);
              return;
            } catch {
              setError(classified);
              return;
            }
          }
        }
        setError(classified);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [tick]);

  return { data, loading, notFound, error, refetch };
}
